import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal, ModalHeader, ModalContent } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Icon } from '@/shared/icons';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import type { HistoryImportRun, HistoryImportRunStatus } from '@troia-v3/types';

import {
  useImportRun,
  useAbortImport,
  HISTORY_IMPORT_TERMINAL_STATUSES,
} from '../../hooks/useHistoryImport';
import { useHistoryImportSocket } from '../../hooks/useHistoryImportSocket';
import { PeriodStep } from './PeriodStep';
import { QrStep, type QrStepQrData } from './QrStep';
import { ChatSelectionStep } from './ChatSelectionStep';
import { ProgressStep } from './ProgressStep';

/**
 * Modal do wizard de "Importar histórico" (WhatsApp gateway) — 4 passos:
 * `period` (escolhe janela + inicia sessão) → `qr` (escaneia/aguarda captura)
 * → `selection` (escolhe chats + período fino) → `progress` (ingestão +
 * resumo final). Aberto pelo `ChannelCard` para canais `gateway-whatsapp`
 * ativos (Task 17).
 *
 * O passo é derivado do `run.status` (fonte única de verdade, atualizada por
 * polling de 5s + socket) — não existe estado de "step" desacoplado do run.
 *
 * @example
 * <ImportHistoryModal isOpen={showImportModal} onClose={() => setShowImportModal(false)} channelId={channel.id} />
 */
export interface ImportHistoryModalProps {
  /** Se o modal está aberto */
  isOpen: boolean;
  /** Callback para fechar o modal */
  onClose: () => void;
  /** Canal (gateway-whatsapp) para o qual o histórico será importado */
  channelId: string;
}

type ImportWizardStep = 'period' | 'qr' | 'selection' | 'progress';

function stepForStatus(runId: string | undefined, status: HistoryImportRunStatus | undefined): ImportWizardStep {
  if (!runId) return 'period';
  if (status === 'ready_for_selection') return 'selection';
  if (status === 'awaiting_qr' || status === 'capturing' || status === undefined) return 'qr';
  // ingesting | importing_media | completed | failed | aborted | expired
  return 'progress';
}

export const ImportHistoryModal: React.FC<ImportHistoryModalProps> = ({ isOpen, onClose, channelId }) => {
  const confirm = useConfirm();
  const [runId, setRunId] = useState<string | undefined>(undefined);
  const [qr, setQr] = useState<QrStepQrData | undefined>(undefined);
  const [livePercent, setLivePercent] = useState<number | undefined>(undefined);
  const [abortFailed, setAbortFailed] = useState(false);

  const queryClient = useQueryClient();
  const { data: run } = useImportRun(runId);
  const abortMutation = useAbortImport();

  useHistoryImportSocket(runId, {
    onQr: (data) => setQr({ qrCode: data.qrCode, expires: data.expires }),
    onStatus: (data) => setLivePercent(data.percentComplete),
    onProgress: (data) => setLivePercent(data.percentComplete),
    onCompleted: (data) => setLivePercent(data.percentComplete),
    onFailed: (data) => setLivePercent(data.percentComplete),
  });

  // Gatilho REDUNDANTE de invalidação (review round 2, Médio 1): o invalidate
  // de ['conversations'] no socket handler não dispara se o socket estiver
  // reconectando quando `history-import:completed` for emitido — o polling de
  // 5s do useImportRun ainda traz o status. Observa a TRANSIÇÃO para
  // 'completed' via ref do status anterior; invalidar 2× é inofensivo.
  const prevStatusRef = useRef<HistoryImportRunStatus | undefined>(undefined);
  useEffect(() => {
    const status = run?.status;
    if (status === 'completed' && prevStatusRef.current !== 'completed') {
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    }
    prevStatusRef.current = status;
  }, [run?.status, queryClient]);

  const resetState = (): void => {
    setRunId(undefined);
    setQr(undefined);
    setLivePercent(undefined);
    setAbortFailed(false);
    prevStatusRef.current = undefined;
  };

  const closeAndReset = (): void => {
    resetState();
    onClose();
  };

  const handleRequestClose = async (): Promise<void> => {
    const isActiveRun = !!run && !HISTORY_IMPORT_TERMINAL_STATUSES.has(run.status);
    if (runId && isActiveRun) {
      const confirmed = await confirm({
        message: 'Uma importação está em andamento. Fechar agora vai cancelar o processo. Deseja continuar?',
        confirmButtonText: 'Fechar',
        variant: 'default',
      });
      if (!confirmed) return;
      setAbortFailed(false);
      abortMutation.mutate(runId, {
        onSuccess: closeAndReset,
        onError: () => setAbortFailed(true),
      });
      return;
    }
    closeAndReset();
  };

  const handleFinish = (): void => {
    resetState();
    onClose();
  };

  const step = stepForStatus(runId, run?.status);

  return (
    <Modal isOpen={isOpen} onClose={handleRequestClose} maxWidth="lg">
      <div data-testid="import-history-modal">
        <ModalHeader onClose={handleRequestClose}>
          <Icon name="download" className="w-5 h-5" style={{ color: '#8b5cf6' }} />
          <h2 className="text-base font-semibold" style={{ color: '#1e293b' }}>
            Importar histórico
          </h2>
        </ModalHeader>
        <ModalContent>
          {abortFailed && (
            <div
              className="rounded-lg border p-3 mb-4 flex items-center gap-3"
              style={{ borderColor: '#fecaca', backgroundColor: '#fef2f2' }}
              data-testid="abort-import-error"
            >
              <Icon name="alert-circle" className="w-4 h-4 flex-shrink-0" style={{ color: '#ef4444' }} />
              <p className="text-xs flex-1" style={{ color: '#b91c1c' }}>
                Não foi possível cancelar a importação. Tente novamente.
              </p>
              <Button
                variant="secondary"
                size="xs"
                onClick={closeAndReset}
                data-testid="force-close-button"
              >
                Fechar mesmo assim
              </Button>
            </div>
          )}
          {step === 'period' && (
            <PeriodStep channelId={channelId} onStarted={(startedRun: HistoryImportRun) => setRunId(startedRun.id)} />
          )}
          {step === 'qr' && <QrStep run={run} qr={qr} />}
          {step === 'selection' && runId && run && <ChatSelectionStep runId={runId} run={run} />}
          {step === 'progress' && run && (
            <ProgressStep run={run} livePercent={livePercent} onFinish={handleFinish} />
          )}
        </ModalContent>
      </div>
    </Modal>
  );
};

ImportHistoryModal.displayName = 'ImportHistoryModal';
